/**
 * Algolia Search Client Configuration
 * 
 * Initializes the Algolia search client for case-specific journal entry search.
 * Uses the 'lite' client for search-only operations (no indexing from frontend).
 * 
 * Prerequisites:
 * - "Search with Algolia" Firebase Extension installed and configured
 * - journalEntries collection syncing to Algolia index
 * - VITE_ALGOLIA_APP_ID and VITE_ALGOLIA_SEARCH_ONLY_API_KEY set in environment
 * 
 * Security:
 * - Uses search-only API key (cannot modify index)
 * - Case-specific filtering enforced at component level via Configure widget
 * - caseId must be configured as "Attribute for faceting" in Algolia Dashboard
 */

import algoliasearch from 'algoliasearch/lite';

// Load environment variables
const appId = import.meta.env.VITE_ALGOLIA_APP_ID;
const apiKey = import.meta.env.VITE_ALGOLIA_SEARCH_ONLY_API_KEY;

// Validate configuration
if (!appId || !apiKey) {
  throw new Error(
    'Algolia configuration missing: VITE_ALGOLIA_APP_ID and VITE_ALGOLIA_SEARCH_ONLY_API_KEY must be set in environment variables'
  );
}

/**
 * Algolia search client instance
 * 
 * Configured with search-only API key for security.
 * Uses the 'lite' client which is optimized for search operations.
 */
export const searchClient = algoliasearch(appId, apiKey);

/**
 * Index name for journal entries
 * 
 * This must match the index name configured in the Firebase Extension.
 * Default: 'journalEntries' (from Firebase Extension setup)
 * 
 * The index should have:
 * - Searchable attributes: title, narrative, summary, aiTags
 * - Attributes for faceting: caseId, userId
 */
export const INDEX_NAME = 'journalEntries';

/**
 * Type definition for journal entry search hits
 * 
 * Matches the structure of documents in the journalEntries Firestore collection
 * after being indexed by Algolia.
 */
export type JournalEntryHit = {
  objectID: string;
  caseId: string;
  userId: string;
  title: string;
  narrative: string;
  summary?: string; // AI-generated summary (from Phase 1 backend)
  tags?: string[]; // User-approved tags (includes accepted AI suggestions)
  aiTags?: string[]; // Legacy field - deprecated in favor of 'tags'
  occurredAt?: number; // Timestamp
  createdAt?: number;
  updatedAt?: number;
  
  // Algolia-specific fields (added during search)
  _highlightResult?: {
    title?: { value: string };
    narrative?: { value: string };
    summary?: { value: string };
    tags?: { value: string }[];
    aiTags?: { value: string }[];
  };
  
  _snippetResult?: {
    narrative?: { value: string };
    summary?: { value: string };
  };
};

/**
 * Configuration constants for Algolia search
 */
export const ALGOLIA_CONFIG = {
  hitsPerPage: 20,
  attributesToHighlight: ['title', 'narrative', 'summary', 'tags'],
  attributesToSnippet: ['narrative:50', 'summary:30'],
} as const;
